import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { contemBusca } from "@/lib/busca";

export interface OpcaoBusca {
  value: string;
  label: string;
  /** Texto secundário à direita (ex.: e-mail, CNPJ); também entra na busca. */
  detalhe?: string;
  /** Opção "vazia" (Sem departamento, Todos…) exibida em cinza. */
  neutra?: boolean;
}

const filtrar = (_value: string, busca: string, keywords?: string[]) =>
  contemBusca((keywords ?? []).join(" "), busca) ? 1 : 0;

/**
 * Select com campo de busca, para listas longas e que crescem (colaboradores, tomadores, departamentos).
 * Mesmo visual do SelectTrigger; listas fixas e curtas continuam no Select comum.
 */
export function SelectBusca({
  opcoes,
  value,
  onChange,
  placeholder = "Selecione",
  placeholderBusca = "Buscar...",
  vazio = "Nenhum resultado.",
  disabled,
  id,
  className,
  prefixo,
  "aria-label": ariaLabel,
}: {
  opcoes: OpcaoBusca[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  placeholderBusca?: string;
  vazio?: string;
  disabled?: boolean;
  id?: string;
  /** Classes do gatilho, como no SelectTrigger. */
  className?: string;
  /** Conteúdo fixo antes do valor (ícone/rótulo de filtro). */
  prefixo?: ReactNode;
  "aria-label"?: string;
}) {
  const [open, setOpen] = useState(false);
  const selecionada = opcoes.find((o) => o.value === value);

  return (
    // modal: dentro de Dialog, sem isso a lista não rola nem recebe foco.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          {prefixo}
          <span className={cn("min-w-0 flex-1 truncate", (!selecionada || selecionada.neutra) && "text-muted-foreground")}>
            {selecionada ? selecionada.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        <Command filter={filtrar}>
          <CommandInput placeholder={placeholderBusca} />
          <CommandList>
            <CommandEmpty>{vazio}</CommandEmpty>
            <CommandGroup>
              {opcoes.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.value}
                  keywords={[o.label, o.detalhe ?? ""]}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className={cn("truncate", o.neutra && "text-muted-foreground")}>{o.label}</span>
                  {o.detalhe && <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{o.detalhe}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
